import { NgClass, AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnInit, Renderer2, inject, viewChild } from '@angular/core';
import { NavigationStart, RouterOutlet } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { debounceTime, filter, fromEvent } from 'rxjs';
import { UserDetails } from '@bn/api-interfaces';
import { AuthService } from '@bn/auth/services';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { MenuItem } from '@bn/layout';
import { MobileMenuComponent } from '@bn/layout/mobile-menu/mobile-menu.component';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { BillingType } from '@bn/shared/entities/enums';
import { ContentService, VersionCheckService } from '@bn/shared/services';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CentralWidgetComponent } from '../central-widget/central-widget.component';
import { DroppableDirective } from '@bn/web-ui/drop-files/droppable.directive';
import { TopbarComponent } from '@bn/web-ui/topbar/topbar.component';
import { mobileMenuConfig } from '../mobile-menu/mobile-menu.config';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SurveyWidgetComponent } from '../survey-widget/survey-widget.component';
import { UpdateNotificationComponent } from '../update-notification/update-notification.component';

@Component({
  selector: 'bn-dashboard-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    TopbarComponent,
    DroppableDirective,
    SidebarComponent,
    FaIconComponent,
    ButtonComponent,
    NgClass,
    RouterOutlet,
    CentralWidgetComponent,
    UpdateNotificationComponent,
    SurveyWidgetComponent,
    MobileMenuComponent,
    AsyncPipe,
  ],
})
export class DashboardLayoutComponent extends AbstractLazyDirective implements OnInit, AfterViewInit {
  private readonly renderer2 = inject(Renderer2);
  private readonly authService = inject(AuthService);
  readonly contentService = inject(ContentService);
  readonly versionCheckService = inject(VersionCheckService);

  public isMobileMenuOpened = false;
  public userDetails: UserDetails | null = null;
  public readonly mobileMenuItems: MenuItem[] = mobileMenuConfig;

  private readonly bnMobileMenu = viewChild('bnMobileMenu', { read: ElementRef });
  private readonly bnSidebar = viewChild('bnSidebar', { read: ElementRef });

  constructor() {
    super();

    this.contentService.getContentData().subscribe(({ showSidebar }) => {
      // ignore undefined, must be explicitly NULL
      if (showSidebar === undefined) return;

      const isShown = showSidebar === null ? undefined : showSidebar;

      if (isShown) {
        this.renderer2.addClass(document.body, 'overflow-hidden');
      } else {
        this.renderer2.removeClass(document.body, 'overflow-hidden');
      }
    });
  }

  public ngOnInit() {
    this.subscriptions.add(
      this.router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe(() => {
        if (this.isMobileMenuOpened) this.onToggleMenu(false);
      }),
    );

    this.subscriptions.add(
      this.userDetailsService.getUserDetails().subscribe((userDetails: UserDetails | null) => {
        this.userDetails = userDetails;
      }),
    );
  }

  public onToggleMenu(isOpened: boolean) {
    this.isMobileMenuOpened = isOpened;
  }

  public logout(): void {
    this.authService.logout();
  }

  public shouldShowNegativeBalanceBanner(): boolean {
    if (!this.userDetails) return false;

    // Don't show banner if account is already disabled (modal will show instead)
    if (this.userDetails.AccountDisabled) return false;

    // Don't show for trial or postpaid users
    if (this.userDetails.BillingType === BillingType.FreeTrial || this.userDetails.BillingType === BillingType.PostPaidPayAsYouGo) {
      return false;
    }

    // Show only for prepaid users with negative balance
    return this.userDetails.Balance < 0;
  }

  public topUpAccount(): void {
    this.router.navigate([`/${AppRoutes.Account}/${AppRoutes.Billing}`]);
  }

  /**
   * Set css variables now and on every window.resize event for:
   *
   * --bn-mobile-menu-height
   * --bn-sidebar-width
   * --bn-sidebar-padding-bottom: adjust so menu can be fully visible when scrolled down on small displays
   */
  public ngAfterViewInit() {
    const mobileMenuHeightElement = this.bnMobileMenu()?.nativeElement?.querySelector('ul.bn-mobile-menu__items');
    const bnSidebar = this.bnSidebar();
    const bnSidebarItem = bnSidebar?.nativeElement?.querySelector('aside');
    const bnSidebarBottom = bnSidebar?.nativeElement?.querySelector('.bn-sidebar__bottom');

    this.contentService.setRootElementStyleProperty('--bn-mobile-menu-height', `${mobileMenuHeightElement.offsetHeight}px`);
    this.contentService.setRootElementStyleProperty('--bn-sidebar-width', `${bnSidebarItem.offsetWidth}px`);
    this.contentService.setRootElementStyleProperty('--bn-sidebar-padding-bottom', `${bnSidebarBottom.offsetHeight}px`);

    this.subscriptions.add(
      fromEvent(window, 'resize')
        .pipe(debounceTime(200))
        .subscribe(() => {
          this.contentService.setRootElementStyleProperty('--bn-mobile-menu-height', `${mobileMenuHeightElement.offsetHeight}px`);
          this.contentService.setRootElementStyleProperty('--bn-sidebar-width', `${bnSidebarItem.offsetWidth}px`);
          this.contentService.setRootElementStyleProperty('--bn-sidebar-padding-bottom', `${bnSidebarBottom.offsetHeight}px`);

          if (this.isMobileMenuOpened) this.onToggleMenu(false);
        }),
    );
  }
}
